import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import TutorialCard from './TutorialCard';
import { tutorialsData } from '../data/tutorialsData';

const { FiSearch, FiFilter, FiClock, FiX, FiArrowLeft } = FiIcons;

const TutorialsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('difficulty'); // difficulty, title, duration
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterDifficulty, setFilterDifficulty] = useState('all');

  // Get unique categories and difficulties
  const categories = ['all', ...new Set(tutorialsData.map(tutorial => tutorial.category))];
  const difficulties = ['all', ...new Set(tutorialsData.map(tutorial => tutorial.difficulty))];

  // Filter and sort tutorials
  const filteredAndSortedTutorials = tutorialsData
    .filter(tutorial => {
      const matchesSearch = tutorial.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          tutorial.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          tutorial.tool.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = filterCategory === 'all' || tutorial.category === filterCategory;
      const matchesDifficulty = filterDifficulty === 'all' || tutorial.difficulty === filterDifficulty;
      
      return matchesSearch && matchesCategory && matchesDifficulty;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'difficulty':
          const difficultyOrder = { 'Beginner': 1, 'Intermediate': 2, 'Advanced': 3 };
          return difficultyOrder[a.difficulty] - difficultyOrder[b.difficulty];
        case 'title':
          return a.title.localeCompare(b.title);
        case 'duration':
          const getDurationMinutes = (duration) => parseInt(duration.match(/\d+/)[0]);
          return getDurationMinutes(a.duration) - getDurationMinutes(b.duration);
        default:
          return 0;
      }
    });

  // Calculate total duration
  const totalDuration = tutorialsData.reduce((total, tutorial) => {
    const minutes = parseInt(tutorial.duration.match(/\d+/)[0]);
    return total + minutes;
  }, 0);

  const formatTotalDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return hours > 0 ? `${hours}h ${remainingMinutes}m` : `${remainingMinutes}m`;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        type: "spring",
        stiffness: 100,
        damping: 12
      }
    }
  };

  return (
    <div className="app">
      <div className="stars"></div>
      
      <main className="main">
        <div className="container">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            style={{ marginBottom: '20px' }}
          >
            <Link
              to="/"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '50px',
                padding: '12px 20px',
                color: '#ffffff',
                fontSize: '16px',
                fontWeight: '500',
                textDecoration: 'none',
                backdropFilter: 'blur(20px)',
                transition: 'all 0.3s ease',
                width: 'fit-content'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 140, 0, 0.1)';
                e.target.style.borderColor = '#ff8c00';
                e.target.style.boxShadow = '0 0 20px rgba(255, 140, 0, 0.2)';
                e.target.style.transform = 'translateX(-5px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                e.target.style.boxShadow = 'none';
                e.target.style.transform = 'translateX(0)';
              }}
            >
              <SafeIcon 
                icon={FiArrowLeft} 
                style={{ 
                  fontSize: '18px',
                  transition: 'transform 0.3s ease'
                }} 
              />
              <span>Back to Home</span>
            </Link>
          </motion.div>

          {/* Header Section */}
          <motion.div 
            className="header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="logo">
              <div className="alien-icon">🎓</div>
              <h1 style={{
                fontSize: '38px',
                fontWeight: '800'
              }}>Tutorials</h1>
            </div>
            <p className="tagline">
              Master your tools with step-by-step video guides. 
              From beginner basics to advanced techniques.
            </p>
            
            {/* Stats */}
            <motion.div 
              className="category-tabs"
              style={{ justifyContent: 'center', marginBottom: '20px' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              <div className="tab" style={{ cursor: 'default' }}>
                <SafeIcon icon={FiClock} style={{ color: '#ff8c00', marginRight: '8px' }} />
                {formatTotalDuration(totalDuration)} Total Content
              </div>
              <div className="tab" style={{ cursor: 'default' }}>
                📚 {tutorialsData.length} Tutorials
              </div>
            </motion.div>

            {/* Search and Filter Section */}
            <motion.div 
              style={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: '16px',
                maxWidth: '800px',
                margin: '0 auto',
                width: '100%'
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              {/* Search Bar */}
              <div className="search-container">
                <motion.div 
                  className="search-wrapper"
                  whileHover={{ scale: 1.02 }}
                  whileFocus={{ scale: 1.02 }}
                >
                  <input
                    type="text"
                    placeholder="Search tutorials across the universe..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  <div className="search-icon">🔍</div>
                </motion.div>
              </div>

              {/* Filters Row */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '12px'
              }}>
                {/* Sort Dropdown */}
                <motion.div whileHover={{ scale: 1.02 }}>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '50px',
                      color: '#ffffff',
                      fontSize: '14px',
                      backdropFilter: 'blur(20px)',
                      cursor: 'pointer',
                      outline: 'none',
                      appearance: 'none'
                    }}
                  >
                    <option value="difficulty" style={{ background: '#000', color: '#fff' }}>📊 By Difficulty</option>
                    <option value="title" style={{ background: '#000', color: '#fff' }}>🔤 Alphabetical</option>
                    <option value="duration" style={{ background: '#000', color: '#fff' }}>⏱️ By Duration</option>
                  </select>
                </motion.div>

                {/* Category Filter */}
                <motion.div whileHover={{ scale: 1.02 }}>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '50px',
                      color: '#ffffff',
                      fontSize: '14px',
                      backdropFilter: 'blur(20px)',
                      cursor: 'pointer',
                      outline: 'none',
                      appearance: 'none'
                    }}
                  >
                    {categories.map(category => (
                      <option key={category} value={category} style={{ background: '#000', color: '#fff' }}>
                        {category === 'all' ? '📂 All Categories' : `📁 ${category}`}
                      </option>
                    ))}
                  </select>
                </motion.div>

                {/* Difficulty Filter */}
                <motion.div whileHover={{ scale: 1.02 }}>
                  <select
                    value={filterDifficulty}
                    onChange={(e) => setFilterDifficulty(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'rgba(255, 255, 255, 0.1)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '50px',
                      color: '#ffffff',
                      fontSize: '14px',
                      backdropFilter: 'blur(20px)',
                      cursor: 'pointer',
                      outline: 'none',
                      appearance: 'none'
                    }}
                  >
                    {difficulties.map(difficulty => (
                      <option key={difficulty} value={difficulty} style={{ background: '#000', color: '#fff' }}>
                        {difficulty === 'all' ? '🎯 All Levels' : 
                         difficulty === 'Beginner' ? '🟢 Beginner' :
                         difficulty === 'Intermediate' ? '🟡 Intermediate' : '🔴 Advanced'}
                      </option>
                    ))}
                  </select>
                </motion.div>
              </div>
            </motion.div>

            {/* Results Count */}
            <motion.div 
              style={{ textAlign: 'center', marginTop: '20px' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.6 }}
            >
              <p style={{ color: '#a0a0a0', fontSize: '16px' }}>
                Showing {filteredAndSortedTutorials.length} of {tutorialsData.length} tutorials
                {searchTerm && (
                  <span style={{ marginLeft: '8px' }}>
                    for "<span style={{ color: '#ff8c00', fontWeight: '600' }}>{searchTerm}</span>"
                  </span>
                )}
                {(filterCategory !== 'all' || filterDifficulty !== 'all') && (
                  <span style={{ marginLeft: '8px', color: '#ff8c00' }}>
                    (filtered)
                  </span>
                )}
              </p>
            </motion.div>
          </motion.div>

          {/* Tutorials Grid */}
          <AnimatePresence mode="wait">
            <motion.div 
              className="tools-grid"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              key={filteredAndSortedTutorials.length}
            >
              {filteredAndSortedTutorials.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="no-results"
                  style={{
                    gridColumn: '1 / -1',
                    textAlign: 'center',
                    padding: '60px 20px',
                    color: '#888',
                    fontSize: '1.2rem'
                  }}
                >
                  <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🎓</div>
                  <div>No tutorials found in this galaxy...</div>
                  <div style={{ fontSize: '0.9rem', marginTop: '10px', opacity: 0.7 }}>
                    Try adjusting your search or filters
                  </div>
                  {(searchTerm || filterCategory !== 'all' || filterDifficulty !== 'all') && (
                    <motion.button
                      onClick={() => {
                        setSearchTerm('');
                        setFilterCategory('all');
                        setFilterDifficulty('all');
                      }}
                      className="btn btn-primary"
                      style={{ 
                        marginTop: '20px',
                        background: 'linear-gradient(135deg, #ff8c00 0%, #ffa500 100%)'
                      }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Clear All Filters
                    </motion.button>
                  )}
                </motion.div>
              ) : (
                filteredAndSortedTutorials.map((tutorial, index) => (
                  <motion.div
                    key={tutorial.slug}
                    variants={itemVariants}
                    layout
                    transition={{ duration: 0.3 }}
                  >
                    <TutorialCard tutorial={tutorial} />
                  </motion.div>
                ))
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default TutorialsPage;